import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProducts } from "features/emailSuite/emailSuiteSlice";
// import customInput from "../../../assets/scss/EmailSuiteOrder.module.scss"; // File sudah dihapus
import eccs from "../../../assets/images/icons/email-suite/ic-eccs.png";
import gsuite from "../../../assets/images/icons/email-suite/ic-gsuite.png";
import microsoft from "../../../assets/images/icons/email-suite/Microsoft_365.png";
import titan from "../../../assets/images/icons/email-suite/titanEmail.png";
import Spinner from "react-bootstrap/Spinner";
import {
  addToCart,
  changeProductFromCart,
} from "features/common/layouts/cartSlice";
import { toast } from "react-toastify";
import { useQuery } from "helpers/utils";
import { addDomainToCart } from "features/common/layouts/cartSlice";
import EmailSuiteEccs from "../EmailSuiteEccs";
import EmailSuiteMicrosoft from "../EmailSuiteMicrosoft";
import EmailSuiteGoogle from "../EmailSuiteGoogle";
import EmailSuiteTitan from "../EmailSuiteTitan";
import { notifyWarning } from "components/ToastMessage";
import { setOrderParams } from "features/common/layouts/cartSlice";
import { useLocation } from "react-router-dom";

// Email Suite Types Configuration
const EMAIL_SUITE_TYPES = {
  ECCS: {
    name: "eccs",
    displayName: "EC Collaboration Suite",
    icon: eccs,
    productIds: "674",
    defaultProductId: 263,
    defaultProductName: "ECCS Email",
    defaultPeriod: "monthly",
    configOptions: ["Akun", "Alias", "Milis"],
    customFields: [],
    validation: {
      minAccounts: 1,
      minAlias: 0,
      minMailing: 0,
      requireDomain: true,
    }
  },
  MICROSOFT: {
    name: "microsoft",
    displayName: "Microsoft 365",
    icon: microsoft,
    productIds: "297,298,299",
    defaultProductId: 297,
    defaultProductName: "Microsoft 365 Business Basic",
    defaultPeriod: "monthly",
    configOptions: ["Akun"],
    customFields: ["DomainPrefix"],
    validation: {
      minAccounts: 1,
      requireDomain: true,
      requireDomainPrefix: true,
    }
  },
  TITAN: {
    name: "titan",
    displayName: "Titan Email",
    icon: titan,
    productIds: "705",
    defaultProductId: 924,
    defaultProductName: "Titan Email Pro",
    defaultPeriod: "monthly",
    configOptions: ["Email Accounts"],
    customFields: [],
    validation: {
      minAccounts: 1,
      requireDomain: true,
    }
  },
  GSUITE: {
    name: "gsuite",
    displayName: "Google Workspace",
    icon: gsuite,
    productIds: "74,76,503,999,1000,1001,1077,1078,1079",
    defaultProductId: 74,
    defaultProductName: "Google Workspace Business Starter",
    defaultPeriod: "monthly",
    configOptions: ["Jumlah User Tambahan"],
    customFields: ["G Suite Domain", "Akun Utama Untuk Google Administrator"],
    validation: {
      minAccounts: 4,
      requireDomain: true,
      requireMainAccount: true,
    }
  }
};

// Form State Manager
const useEmailSuiteFormManager = () => {
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedProductName, setSelectedProductName] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("monthly");
  const [domain, setDomain] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);

  // Configuration and custom fields state
  const [configOptions, setConfigOptions] = useState({});
  const [customFields, setCustomFields] = useState({});
  const [formValues, setFormValues] = useState({});

  const resetForm = () => {
    setSelectedProduct("");
    setSelectedProductName("");
    setSelectedPeriod("monthly");
    setDomain("");
    setTotalPrice(0);
    setConfigOptions({});
    setCustomFields({});
    setFormValues({});
  };

  const updateFormValues = (updates) => {
    setFormValues(prev => ({ ...prev, ...updates }));
  };

  const updateConfigOptions = (updates) => {
    setConfigOptions(prev => ({ ...prev, ...updates }));
  };

  const updateCustomFields = (updates) => {
    setCustomFields(prev => ({ ...prev, ...updates }));
  };

  return {
    selectedForm,
    setSelectedForm,
    selectedProduct,
    setSelectedProduct,
    selectedProductName,
    setSelectedProductName,
    selectedPeriod,
    setSelectedPeriod,
    domain,
    setDomain,
    totalPrice,
    setTotalPrice,
    configOptions,
    customFields,
    formValues,
    resetForm,
    updateFormValues,
    updateConfigOptions,
    updateCustomFields,
  };
};

// Validation Manager
const useValidationManager = () => {
  const validateDomain = (domain) => {
    const invalidChars = /[<>|!;[\]}?:`/]/;
    const domainParts = domain.split('.');

    if (invalidChars.test(domain)) {
      return { isValid: false, message: "Maaf, Domain Anda tidak valid!!" };
    }

    if (!domain) {
      return { isValid: false, message: "Domain Tidak Boleh Kosong!" };
    }

    if (domainParts.length < 2 || !domain.includes('.')) {
      return { isValid: false, message: "Maaf, Domain Anda tidak valid! Pastikan memiliki ekstensi domain." };
    }

    return { isValid: true };
  };

  const validateDomainPrefix = (domainPrefix) => {
    const invalidChars = /[<>|!;[\]}?:`/]/;

    if (invalidChars.test(domainPrefix)) {
      return { isValid: false, message: "Maaf, Domain Prefix Anda tidak valid!!" };
    }

    if (!domainPrefix) {
      return { isValid: false, message: "Domain Prefix Tidak Boleh Kosong!" };
    }

    return { isValid: true };
  };

  const validateAccounts = (count, minCount, type = "Akun") => {
    if (count <= 0 || isNaN(count)) {
      return { isValid: false, message: `Maaf, Jumlah ${type} tidak boleh kurang dari ${minCount}!` };
    }
    return { isValid: true };
  };

  const validateForm = (formType, values) => {
    const config = EMAIL_SUITE_TYPES[formType.toUpperCase()];
    if (!config) return { isValid: false, message: "Invalid form type" };

    const validations = [];

    // Domain validation
    if (config.validation.requireDomain) {
      const domainValidation = validateDomain(values.domain);
      if (!domainValidation.isValid) {
        validations.push(domainValidation);
      }
    }

    // Domain prefix validation for Microsoft
    if (formType === "microsoft" && config.validation.requireDomainPrefix) {
      const domainPrefix = values.DomainPrefix;
      const prefixValidation = validateDomainPrefix(domainPrefix);
      if (!prefixValidation.isValid) {
        validations.push(prefixValidation);
      }
    }

    // Account validation
    if (values.accounts !== undefined) {
      const accountValidation = validateAccounts(values.accounts, config.validation.minAccounts);
      if (!accountValidation.isValid) {
        validations.push(accountValidation);
      }
    }

    // Additional validations for specific forms
    if (formType === "gsuite" && config.validation.requireMainAccount) {
      if (!values.mainAccount) {
        validations.push({ isValid: false, message: "Maaf, Akun utama untuk Google Administrator Tidak Boleh Kosong!" });
      }
    }

    return {
      isValid: validations.length === 0,
      errors: validations
    };
  };

  return {
    validateDomain,
    validateDomainPrefix,
    validateAccounts,
    validateForm,
  };
};

// Product Manager
const useProductManager = () => {
  const calculatePrice = (productData, configOptions, selectedPeriod, formValues) => {
    if (!productData) return 0;

    let total = 0;

    // Add base product price (only if > 0)
    const basePrice = productData.pricing?.IDR?.[selectedPeriod];
    if (basePrice && parseFloat(basePrice) > 0) {
      total += parseFloat(basePrice);
    }

    // Add config options pricing (this is the main pricing for email suites)
    if (productData.configoptions && productData.configoptions.configoption) {
      const configOptionsList = productData.configoptions.configoption || [];

      configOptionsList.forEach((config) => {
        config.options.option.forEach((option) => {
          const pricing = option.pricing?.IDR?.[selectedPeriod];
          if (pricing && parseFloat(pricing) > 0) {
            // Try to find the value using different possible keys
            let value = formValues[config.name] || 0;

            // If not found, try alternative names
            if (value === 0) {
              if (config.name === "Jumlah Akun" && formValues["Akun"] !== undefined) {
                value = formValues["Akun"];
              } else if (config.name === "Email Accounts" && formValues["Akun"] !== undefined) {
                value = formValues["Akun"];
              } else if (config.name === "Akun" && formValues["Jumlah Akun"] !== undefined) {
                value = formValues["Jumlah Akun"];
              }
            }

            const optionTotal = parseFloat(pricing) * value;
            total += optionTotal;
          }
        });
      });
    }

    // If no config options pricing, try to get base price even if it's 0
    if (total === 0 && basePrice !== undefined) {
      total = parseFloat(basePrice) || 0;
    }

    return total;
  };

  const buildProductPayload = (formType, productData, formValues, configOptions, customFields) => {
    const config = EMAIL_SUITE_TYPES[formType.toUpperCase()];
    if (!config) return null;

    const calculatedPrice = calculatePrice(productData, configOptions, formValues.selectedPeriod, formValues);

    const payload = {
      pid: productData.pid,
      name: productData.name,
      price: calculatedPrice,
      duration: formValues.selectedPeriod,
      configoptions: [],
      customfield: [],
      type: "emailSuite",
    };

    // Add config options - filter out those with value 0
    Object.entries(configOptions).forEach(([name, id]) => {
      let value = formValues[name];

      // If not found, try alternative names
      if (value === undefined) {
        if (name === "Email Accounts" && formValues["Akun"] !== undefined) {
          value = formValues["Akun"];
        } else if (name === "Jumlah Akun" && formValues["Akun"] !== undefined) {
          value = formValues["Akun"];
        } else if (name === "Akun" && formValues["Jumlah Akun"] !== undefined) {
          value = formValues["Jumlah Akun"];
        }
      }

      // Only add config options with valid values (not 0, null, undefined, or empty)
      if (value !== undefined && value !== 0 && value !== null && value !== '') {
        payload.configoptions.push({
          id,
          value: value
        });
      }
    });

    // Add custom fields
    Object.entries(customFields).forEach(([name, id]) => {
      if (formValues[name] !== undefined) {
        payload.customfield.push({
          id,
          value: formValues[name]
        });
      }
    });

    return payload;
  };

  return {
    calculatePrice,
    buildProductPayload,
  };
};

const EmailSuiteOrder = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const query = useQuery();
  const pid = query.pid;
  const billingCycle = query.billingcycle;
  const toastId = useRef(null);
  const product = useSelector((state) => state.emailSuite);
  const { productsList } = useSelector((state) => state.cart);

  // Custom hooks
  const formManager = useEmailSuiteFormManager();
  const validationManager = useValidationManager();
  const productManager = useProductManager();

  // Initialize order params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get("pid");
    const billingcycle = params.get("billingcycle");
    dispatch(setOrderParams({ pid, billingcycle }));
  }, [location.search, dispatch]);

  // Load products based on PID - Only load once when component mounts
  useEffect(() => {
    // console.log("Loading products...");
    // console.log("Current product state:", product);

    // Only load products if they haven't been loaded yet
    const hasLoadedProducts = Object.values(EMAIL_SUITE_TYPES).some(type =>
      product[type.name] && product[type.name].length > 0
    );

    // console.log("Has loaded products:", hasLoadedProducts);

    if (!hasLoadedProducts) {
      // console.log("Dispatching getProducts for all types");
      Object.values(EMAIL_SUITE_TYPES).forEach(type => {
        // console.log("Loading products for:", type.name, "with IDs:", type.productIds);
        dispatch(getProducts(type.productIds));
      });
    }
  }, []); // Remove pid and dispatch dependencies to prevent re-fetching

  // Set billing cycle from URL
  useEffect(() => {
    if (billingCycle) {
      formManager.setSelectedPeriod(billingCycle);
    }
  }, [billingCycle]);

  // Auto-select form and product based on PID - Only run once when products are loaded
  useEffect(() => {
    // console.log("Auto-selection useEffect triggered");
    // console.log("product.status:", product.status);
    // console.log("pid:", pid);
    // console.log("formManager.selectedForm:", formManager.selectedForm);

    // Check if all required products are loaded
    const allProductsLoaded = Object.values(EMAIL_SUITE_TYPES).every(type =>
      product[type.name] && product[type.name].length > 0
    );

    // console.log("All products loaded:", allProductsLoaded);

    if (product.status === "fulfilled" && allProductsLoaded && pid && !formManager.selectedForm) {
      // console.log("Auto-selecting based on PID:", pid);

      Object.entries(EMAIL_SUITE_TYPES).forEach(([key, config]) => {
        const productList = product[config.name];
        // console.log(`Checking ${config.name} products:`, productList);

        if (productList && productList.length > 0) {
          const foundProduct = productList.find(p => p.pid === parseInt(pid));
          // console.log(`Looking for PID ${pid} in ${config.name}:`, foundProduct);

          if (foundProduct) {
            // console.log("Found product:", foundProduct.name, "in category:", config.name);

            // Set form and product selection
            formManager.setSelectedForm(config.name);
            formManager.setSelectedProduct(pid);
            formManager.setSelectedProductName(foundProduct.name);

            // Set billing cycle from URL or use default
            const selectedBillingCycle = billingCycle || config.defaultPeriod;
            formManager.setSelectedPeriod(selectedBillingCycle);

            // Initialize default form values
            const defaultValues = {};
            if (foundProduct.configoptions && foundProduct.configoptions.configoption) {
              foundProduct.configoptions.configoption.forEach((config) => {
                config.options.option.forEach((option) => {
                  if (config.name === "Jumlah Akun" || config.name === "Email Accounts") {
                    defaultValues["Akun"] = 1;
                  } else {
                    defaultValues[config.name] = 1;
                  }
                });
              });
            }
            formManager.updateFormValues(defaultValues);

            // Initialize config options and custom fields
            const configOptionsMap = {};
            const customFieldsMap = {};

            // Set config options based on product
            if (foundProduct.configoptions && foundProduct.configoptions.configoption) {
              foundProduct.configoptions.configoption.forEach((config) => {
                configOptionsMap[config.name] = config.id;
              });
            }

            // Set custom fields based on product
            if (foundProduct.customfields && foundProduct.customfields.customfield) {
              foundProduct.customfields.customfield.forEach((field) => {
                customFieldsMap[field.name] = field.id;
              });
            }

            formManager.updateConfigOptions(configOptionsMap);
            formManager.updateCustomFields(customFieldsMap);

            // console.log("Auto-selection completed for:", config.name);
            // console.log("Config options:", configOptionsMap);
            // console.log("Custom fields:", customFieldsMap);
          }
        }
      });
    }
  }, [product, pid, billingCycle, formManager.selectedForm]); // Added product dependency to ensure it runs when products are loaded

  // Update cart when configOptions or customFields are populated
  useEffect(() => {
    const existingEmailSuite = productsList.find(item => item.type === "emailSuite");

    if (existingEmailSuite && formManager.selectedForm &&
      (Object.keys(formManager.configOptions).length > 0 || Object.keys(formManager.customFields).length > 0)) {

      const currentForm = formManager.selectedForm;
      const productList = product[currentForm];
      const productData = productList?.find(p => p.pid === parseInt(formManager.selectedProduct));

      if (productData) {
        const productPayload = productManager.buildProductPayload(
          currentForm,
          productData,
          {
            ...formManager.formValues,
            selectedPeriod: formManager.selectedPeriod
          },
          formManager.configOptions,
          formManager.customFields
        );

        if (productPayload) {
          dispatch(changeProductFromCart(productPayload));
        }
      }
    }
  }, [formManager.configOptions, formManager.customFields, formManager.selectedForm, formManager.selectedProduct]);

  // Force re-render when form is auto-selected
  useEffect(() => {
    if (formManager.selectedForm && product[formManager.selectedForm]) {
      // console.log("Form auto-selected:", formManager.selectedForm);
      // console.log("Selected product:", formManager.selectedProduct);
      // console.log("Selected period:", formManager.selectedPeriod);
    }
  }, [formManager.selectedForm, formManager.selectedProduct, formManager.selectedPeriod]);

  const handleFormClick = (formName) => {
    const config = EMAIL_SUITE_TYPES[formName.toUpperCase()];
    if (!config) return;

    // Check if there's already an email suite product in cart
    const existingEmailSuite = productsList.find(item => item.type === "emailSuite");

    // Preserve current values if switching between email suite types
    const preservedValues = {
      selectedPeriod: formManager.selectedPeriod,
      domain: formManager.domain,
      formValues: { ...formManager.formValues },
    };

    // Reset form state
    formManager.resetForm();

    // Load products if not already loaded
    if (!product[formName] || product[formName].length === 0) {
      dispatch(getProducts(config.productIds));
    }

    // Set form
    formManager.setSelectedForm(formName);

    // Use first product in array (index 0) for the new form type
    const productList = product[formName];
    const firstProduct = productList && productList.length > 0 ? productList[0] : null;

    if (firstProduct) {
      formManager.setSelectedProduct(firstProduct.pid);
      formManager.setSelectedProductName(firstProduct.name);
    } else {
      formManager.setSelectedProduct(config.defaultProductId);
      formManager.setSelectedProductName(config.defaultProductName);
    }

    // Initialize config options and custom fields for the new form type
    const configOptionsMap = {};
    const customFieldsMap = {};

    // This will be populated by the child form components when they load
    formManager.updateConfigOptions(configOptionsMap);
    formManager.updateCustomFields(customFieldsMap);

    // Restore preserved values if switching between email suite types
    if (existingEmailSuite) {
      formManager.setSelectedPeriod(preservedValues.selectedPeriod);
      formManager.setDomain(preservedValues.domain);

      // Restore form values that are compatible with new form type
      const compatibleValues = {};
      config.configOptions.forEach(option => {
        if (preservedValues.formValues[option] !== undefined) {
          compatibleValues[option] = preservedValues.formValues[option];
        } else {
          compatibleValues[option] = config.validation.minAccounts || 1;
        }
      });

      // Restore custom fields if compatible
      config.customFields.forEach(field => {
        if (preservedValues.formValues[field] !== undefined) {
          compatibleValues[field] = preservedValues.formValues[field];
        }
      });

      formManager.updateFormValues(compatibleValues);

      // Update cart with new product but preserved values
      setTimeout(() => {
        const productData = firstProduct || productList?.find(p => p.pid === parseInt(config.defaultProductId));

        if (productData) {
          // Wait for child form to initialize configOptions and customFields
          setTimeout(() => {
            // Check if configOptions and customFields are populated
            if (Object.keys(formManager.configOptions).length > 0 || Object.keys(formManager.customFields).length > 0) {
              const productPayload = productManager.buildProductPayload(
                formName,
                productData,
                {
                  ...compatibleValues,
                  selectedPeriod: preservedValues.selectedPeriod
                },
                formManager.configOptions,
                formManager.customFields
              );

              if (productPayload) {
                dispatch(changeProductFromCart(productPayload));
              }
            }
          }, 500); // Longer delay to ensure child form has initialized
        }
      }, 100); // Small delay to ensure products are loaded
    } else {
      // Initialize default values for new email suite
      const defaultValues = {};
      config.configOptions.forEach(option => {
        defaultValues[option] = config.validation.minAccounts || 1;
      });
      formManager.updateFormValues(defaultValues);
    }
  };

  const handleProductChange = (newProductId, newProductName) => {
    formManager.setSelectedProduct(newProductId);
    formManager.setSelectedProductName(newProductName);

    // Recalculate price
    const currentForm = formManager.selectedForm;
    const productList = product[currentForm];
    const productData = productList?.find(p => p.pid === parseInt(newProductId));

    if (productData) {
      const newPrice = productManager.calculatePrice(
        productData,
        formManager.configOptions,
        formManager.selectedPeriod,
        formManager.formValues
      );
      formManager.setTotalPrice(newPrice);

      // Update cart if product is already in cart
      const existingEmailSuite = productsList.find(item => item.type === "emailSuite");
      if (existingEmailSuite) {
        const productPayload = productManager.buildProductPayload(
          currentForm,
          productData,
          {
            ...formManager.formValues,
            selectedPeriod: formManager.selectedPeriod
          },
          formManager.configOptions,
          formManager.customFields
        );

        if (productPayload) {
          dispatch(changeProductFromCart(productPayload));
        }
      }
    }
  };

  const handlePeriodChange = (newPeriod) => {
    formManager.setSelectedPeriod(newPeriod);

    // Recalculate price
    const currentForm = formManager.selectedForm;
    const productList = product[currentForm];
    const productData = productList?.find(p => p.pid === parseInt(formManager.selectedProduct));

    if (productData) {
      const newPrice = productManager.calculatePrice(
        productData,
        formManager.configOptions,
        newPeriod,
        formManager.formValues
      );
      formManager.setTotalPrice(newPrice);

      // Update cart if product is already in cart
      const existingEmailSuite = productsList.find(item => item.type === "emailSuite");
      if (existingEmailSuite) {
        const productPayload = productManager.buildProductPayload(
          currentForm,
          productData,
          {
            ...formManager.formValues,
            selectedPeriod: newPeriod
          },
          formManager.configOptions,
          formManager.customFields
        );

        if (productPayload) {
          dispatch(changeProductFromCart(productPayload));
        }
      }
    }
  };

  const handleAddToCart = (e) => {
    e.preventDefault();

    const currentForm = formManager.selectedForm;
    const config = EMAIL_SUITE_TYPES[currentForm.toUpperCase()];
    if (!config) return;

    // Validate form
    const validation = validationManager.validateForm(currentForm, {
      ...formManager.formValues,
      domain: formManager.domain,
      selectedPeriod: formManager.selectedPeriod
    });

    if (!validation.isValid) {
      validation.errors.forEach(error => {
        notifyWarning(error.message);
      });
      return;
    }

    // Get product data
    const productList = product[currentForm];
    const productData = productList?.find(p => p.pid === parseInt(formManager.selectedProduct));

    if (!productData) {
      notifyWarning("Product data not found");
      return;
    }

    // Build product payload
    const productPayload = productManager.buildProductPayload(
      currentForm,
      productData,
      {
        ...formManager.formValues,
        selectedPeriod: formManager.selectedPeriod
      },
      formManager.configOptions,
      formManager.customFields
    );

    if (!productPayload) {
      notifyWarning("Failed to build product payload");
      return;
    }

    // Add domain to cart if required
    if (config.validation.requireDomain) {
      dispatch(addDomainToCart({ domain: formManager.domain, type: "domain-existing" }));
    }

    // Check if email suite already exists in cart
    const existingEmailSuite = productsList.find(item => item.type === "emailSuite");

    if (existingEmailSuite) {
      dispatch(changeProductFromCart(productPayload));
    } else {
      dispatch(addToCart(productPayload));
    }

    // Show success message
    toast.success("Produk berhasil ditambahkan ke keranjang", {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 3000,
    });
  };

  const renderForm = () => {
    // console.log("renderForm called");
    // console.log("selectedForm:", formManager.selectedForm);
    // console.log("product[selectedForm]:", product[formManager.selectedForm]);
    // console.log("product status:", product.status);

    if (!formManager.selectedForm || !product[formManager.selectedForm]) {
      // console.log("Form not rendered - missing selectedForm or product data");
      return null;
    }

    const formProps = {
      selectedProduct: formManager.selectedProduct,
      selectedProductName: formManager.selectedProductName,
      selectedPeriod: formManager.selectedPeriod,
      domain: formManager.domain,
      setDomain: formManager.setDomain,
      totalPrice: formManager.totalPrice,
      setTotalPrice: formManager.setTotalPrice,
      formValues: formManager.formValues,
      updateFormValues: formManager.updateFormValues,
      configOptions: formManager.configOptions,
      updateConfigOptions: formManager.updateConfigOptions,
      customFields: formManager.customFields,
      updateCustomFields: formManager.updateCustomFields,
      handleProductChange,
      handlePeriodChange,
      handleAddToCart,
      product: product[formManager.selectedForm],
      validationManager,
      productManager,
    };

    switch (formManager.selectedForm) {
      case "eccs":
        return <EmailSuiteEccs {...formProps} />;
      case "microsoft":
        return <EmailSuiteMicrosoft {...formProps} />;
      case "titan":
        return <EmailSuiteTitan {...formProps} />;
      case "gsuite":
        return <EmailSuiteGoogle {...formProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="card mb-3 email-suite-card" style={{ minHeight: "100px", borderRadius: "16px", borderColor: "transparent", boxShadow: "0px 0px 4px rgba(190, 190, 190, 0.5)" }}>
        <div className="card-body">
          <div className="form-check py-1 px-4">
            <input
              type="radio"
              className="form-check-input"
              name="emailSuite"
              id="emailSuite"
              value="emailSuite"
              defaultChecked={true}
            />
            <label
              htmlFor="emailSuite"
              className="form-check-label fw-bold email-suite-label"
              style={{ fontSize: "18px", marginLeft: "20px" }}
            >
              Email Suite
            </label>
            <p className="mb-0 mt-2 email-suite-desc" style={{ marginLeft: "21px" }}>
              Pilih layanan email profesional untuk bisnis Anda
            </p>
          </div>
          
          <div className="row mt-3 px-3 px-md-4 g-3">
            {Object.entries(EMAIL_SUITE_TYPES)
              .filter(([key, config]) => config.name !== "eccs" && config.name !== "gsuite")
              .map(([key, config]) => (
                <div
                  key={config.name}
                  className="col-12 col-md-6"
                >
                  <div
                    className={`position-relative h-100 email-suite-option ${
                      formManager.selectedForm === config.name ? "border border-2 border-primary email-suite-selected" : "border"
                    }`}
                    style={{ borderRadius: "8px", cursor: "pointer", overflow: "hidden", padding: "16px" }}
                  onClick={() => handleFormClick(config.name)}
                >
                  <div
                    className={`position-absolute email-suite-checklist-wrapper ${formManager.selectedForm === config.name ? "" : "d-none"
                      }`}
                      style={{ right: "8px", top: "8px" }}
                  >
                      <div className="email-suite-checklist-badge">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="10" cy="10" r="10" className="email-suite-checklist-circle"/>
                          <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                  </div>
                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "120px" }}>
                  <img
                    src={config.icon}
                        style={{ 
                          maxWidth: "200px", 
                          maxHeight: "80px",
                          width: "auto",
                          height: "auto",
                          objectFit: "contain"
                        }}
                    alt={`${config.name}-icon`}
                    className="email-suite-icon"
                  />
                      <p className="text-center mb-0 mt-3 fw-semibold email-suite-option-name" style={{ fontSize: "14px" }}>{config.displayName}</p>
                    </div>
                  </div>
                </div>
              ))}

          </div>

          {(() => {
              if (product.status === "loading") {
                return (
                <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                  </div>
                );
              } else if (formManager.selectedForm) {
                return (
                <div className="mt-3 px-3 px-md-4">{renderForm()}</div>
                );
              } else {
                return null;
              }
            })()}
        </div>
      </div>
    </>
  );
};

export default EmailSuiteOrder;
